import { FieldValues, UseFormGetValues } from 'react-hook-form'
import {
  calculateFPLPercentage,
  coerceFormRuleIdArray,
  evaluateFormRuleExpression,
  evaluateFormRuleValue,
  FormRuleType,
} from '@app-store'
import {
  isCalculableDurationKind,
  normalizeDurationType,
  normalizeDurationValue,
  parseDurationKind,
} from '../calculateExpirationDate.utils'
import { resolveFormAncestors } from '../formAncestry.utils'
import {
  buildFormRuleExpressionHelpers,
  fetchEntityListingForRule,
  fetchRecordDataForRule,
} from '../formBehavior.utils'
import { setRuleCache } from '../formBehaviorRuleCache.registry'
import {
  FORM_RULE_FUNCTIONS,
  isFormRuleFunctionName,
} from '../formRuleFunctions.registry'
import { resolveFormRuleParams } from '../formRuleParamResolver.utils'
import {
  FormBehaviorRuleResult,
  FormRuleActionDef,
  SourceType,
} from '../../types/formBehavior.types'

const emptyResult = (): FormBehaviorRuleResult => ({
  requiredOverrides: {},
  disabledOverrides: {},
  lookupAllowedIds: {},
  categoryAllowedIds: {},
  minDateOverrides: {},
  maxDateOverrides: {},
})

const emptyResultForBlockedFetch = (
  actions: FormRuleActionDef[]
): FormBehaviorRuleResult => {
  const result = emptyResult()
  for (const action of actions) {
    const keyName = action.key_name?.trim()
    if (!keyName) continue
    if (action.type === 'filter_lookup_ids') {
      result.lookupAllowedIds[keyName] = []
    }
    if (action.type === 'filter_category') {
      result.categoryAllowedIds[keyName] = []
    }
    if (action.type === 'set_value') {
      if (!result.fieldValues) result.fieldValues = {}
      result.fieldValues[keyName] = null
    }
  }
  return result
}

const runSetValueAction = async (
  layoutUid: string,
  action: FormRuleActionDef,
  getValues: UseFormGetValues<FieldValues>,
  formInstanceId?: string
): Promise<unknown> => {
  const keyName = action.key_name?.trim()
  const functionName = action.function?.trim()
  if (!keyName || !functionName || !isFormRuleFunctionName(functionName)) {
    return null
  }
  const ancestors = resolveFormAncestors(formInstanceId)
  const args = resolveFormRuleParams(
    layoutUid,
    action.params,
    getValues,
    ancestors
  )
  if (!args) return null
  try {
    if (functionName === 'calculateExpirationDate') {
      const durationKind = parseDurationKind(args[0])
      if (durationKind === 'MANUAL' || durationKind === 'INDEFINITE') {
        return undefined
      }
      if (!isCalculableDurationKind(durationKind)) return null
      const durationType = normalizeDurationType(args[0])
      const durationValue = normalizeDurationValue(args[1])
      const startDate = typeof args[2] === 'string' ? args[2] : null
      if (!durationType || durationValue == null || !startDate) return null
      return FORM_RULE_FUNCTIONS.calculateExpirationDate(
        durationType,
        durationValue,
        startDate
      )
    }
    if (functionName === 'calculateFPLPercentage') {
      const res = await calculateFPLPercentage(
        args[0] as string,
        args[1] as string,
        args[2] as string
      )
      return res?.data?.fplPercentage ?? null
    }
    const fn = FORM_RULE_FUNCTIONS[functionName] as (
      ...fnArgs: unknown[]
    ) => unknown
    const value = fn(...args)
    if (value == null) return null
    if (functionName === 'toLookupRef') return value
    if (functionName === 'getValue') return String(value)
    return value
  } catch (err) {
    console.error('[FormBehaviorEngine] set_value failed', functionName, err)
    return null
  }
}

export const handleFormBehaviorRule = async (
  layoutUid: string,
  rule: FormRuleType,
  getValues: UseFormGetValues<FieldValues>,
  options?: { skipSetValue?: boolean; formInstanceId?: string }
): Promise<FormBehaviorRuleResult | null> => {
  const actions =
    (rule.then as { actions?: FormRuleActionDef[] })?.actions ?? []
  const source = rule.source as SourceType | undefined
  const cacheKey = source?.cache_as?.trim()
  const hasListingSource =
    !!source?.entity_name && source.type === 'entity_listing'
  const hasRecordSource = !!source?.entity_name && source.type === 'record_data'

  const ancestors = resolveFormAncestors(options?.formInstanceId)
  const expressionHelpers = buildFormRuleExpressionHelpers(
    layoutUid,
    getValues,
    ancestors
  )

  let rows: FieldValues[] | null = null
  let data: Record<string, unknown> = {}

  if (hasListingSource) {
    rows = await fetchEntityListingForRule(rule, getValues, ancestors)
    if (cacheKey) {
      setRuleCache(layoutUid, cacheKey, rows === null ? [] : rows)
    }
    if (rows === null) {
      return emptyResultForBlockedFetch(actions)
    }
    data = { content: rows ?? [] }
  } else if (hasRecordSource) {
    const res = await fetchRecordDataForRule(rule, getValues, ancestors)
    if (cacheKey) {
      setRuleCache(layoutUid, cacheKey, res)
    }
    data = res
  }

  const result: FormBehaviorRuleResult = emptyResult()

  for (const action of actions) {
    const keyName = action.key_name?.trim()
    if (!keyName) continue
    switch (action.type) {
      case 'required': {
        const expression = action.expression?.trim()
        if (!expression) continue
        const required = evaluateFormRuleExpression(
          expression,
          data,
          expressionHelpers
        )
        if (required !== undefined) {
          result.requiredOverrides[keyName] = required
        }
        break
      }
      case 'disabled': {
        const expression = action.expression?.trim()
        if (!expression) continue
        const disabled = evaluateFormRuleExpression(
          expression,
          data,
          expressionHelpers
        )
        if (disabled !== undefined) {
          result.disabledOverrides[keyName] = disabled
        }
        break
      }
      case 'filter_lookup_ids': {
        const expression = action.expression?.trim()
        if (!expression) continue
        const value = evaluateFormRuleValue(expression, data, expressionHelpers)
        const ids = coerceFormRuleIdArray(value)
        if (ids !== undefined) {
          result.lookupAllowedIds[keyName] = ids
        }
        break
      }
      case 'filter_category': {
        const expression = action.expression?.trim()
        if (!expression) continue
        const value = evaluateFormRuleValue(expression, data, expressionHelpers)
        const ids = coerceFormRuleIdArray(value)
        if (ids !== undefined) {
          result.categoryAllowedIds[keyName] = ids
        }
        break
      }
      case 'set_value': {
        if (options?.skipSetValue) break
        const computed = await runSetValueAction(
          layoutUid,
          action,
          getValues,
          options?.formInstanceId
        )
        if (computed !== undefined) {
          if (!result.fieldValues) result.fieldValues = {}
          result.fieldValues[keyName] = computed as unknown | null
        }
        break
      }
      case 'set_min_date':
      case 'set_max_date': {
        const expression = action.expression?.trim()
        if (!expression) continue
        const applies = evaluateFormRuleExpression(
          expression,
          data,
          expressionHelpers
        )
        if (applies) {
          const computed = await runSetValueAction(
            layoutUid,
            action,
            getValues,
            options?.formInstanceId
          )
          if (typeof computed === 'string' && computed) {
            if (action.type === 'set_min_date') {
              result.minDateOverrides[keyName] = computed
            } else {
              result.maxDateOverrides[keyName] = computed
            }
          }
        } else if (applies === false) {
          if (action.type === 'set_min_date') {
            result.minDateOverrides[keyName] = undefined
          } else {
            result.maxDateOverrides[keyName] = undefined
          }
        }
        break
      }
      default:
        break
    }
  }
  return result
}
